import { useEffect, useRef, useReducer, Reducer, useCallback } from 'react'
import { Canceler } from 'axios'

import { useAPI } from '../api'

export enum Modification {
  Untouched = 0,
  Modified = 1,
  Saved = 2,
}

type ListType = {
  account_name: string
  account_status: string
  id: string
  name: string
  plan: string
}

export type RadarType = {
  id: number
  cache_id: number
  assigned_to: number

  evszam: string
  sorszam: string
  jogtar_kod: string
  kibocsato: string
  text: string
  title: string
  njt_url: string
  kozlony_url: string

  lists: ListType[]

  issue_year: string
  issue_number: number
  issue_date: string

  digest: string
  digest_status: string
  importance: string
  digest_tags: string[]
  enter_into_force: string[]

  isModified: Modification
}

type PageState = {
  radars: RadarType[]
  modified: Array<number>
  isLoading: boolean
  isModified: Modification
}

const actionOnItem = (state: PageState, id: number, action: Function) => {
  const _index = state.radars.findIndex((radar) => radar.id === id)
  if (_index > -1) return action(_index)
  else return state
}

const reducer = (state: PageState, action: any): PageState => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, isLoading: true }
    case 'LOADED':
      return {
        radars: action.payload,
        isLoading: false,
        modified: [],
        isModified: Modification.Untouched,
      }
    case 'MODIFIED':
      const _index = state.radars.findIndex((radar) => radar.id === action.id)
      if (_index > -1) {
        const _radar = Object.assign({}, state.radars[_index], action.changes, {
          isModified: Modification.Modified,
        })
        state.radars[_index] = _radar
        const _modified = state.modified.includes(action.id)
          ? state.modified
          : state.modified.concat(action.id)
        return { ...state, modified: _modified, isModified: Modification.Modified }
      } else return state
    case 'SAVED':
      return actionOnItem(state, action.id, (index: number) => {
        state.radars[index].isModified = Modification.Saved
        return { ...state }
      })
    case 'SAVEDALL':
      return { ...state, modified: [], isModified: Modification.Saved }

    default:
      return state
  }
}

export const useRadar = (onForbidden?: Function, onError?: Function) => {
  const [state, dispatch] = useReducer<Reducer<PageState, any>>(reducer, {
    radars: [],
    isLoading: false,
    modified: [],
    isModified: Modification.Untouched,
  })

  const cancelRef = useRef<Canceler>()
  const { fetchData, updateData } = useAPI({ cancelRef, onError, onForbidden })

  const loadRadarList = () => {
    dispatch({ type: 'LOADING' })

    fetchData(`/radar`)
      .then((resp) => {
        const _radars = resp.data.map((item) => {
          const _tags =
            item.digest_tags && item.digest_tags.length > 0 ? item.digest_tags?.split('|') : []
          const _enter_into_force =
            item.enter_into_force && item.enter_into_force.length > 0
              ? item.enter_into_force?.split('|')
              : []
          return {
            ...item,
            digest_tags: _tags,
            enter_into_force: _enter_into_force,
            isModified: Modification.Untouched,
          }
        })
        dispatch({ type: 'LOADED', payload: _radars })
      })
      .catch((error: any) => {})
  }

  const _updateRadarItem = useCallback(
    (id: number, changes: any) => {
      dispatch({ type: 'MODIFIED', id, changes })
    },
    [dispatch],
  )

  const _saveRadarItems = () => {
    const _radars = state.radars
      .filter((radar) => state.modified.includes(radar.id))
      .map((item) => {
        const _tags = item.digest_tags?.join('|') || ''
        const _enter_into_force = item.enter_into_force?.join('|') || ''
        return {
          id: item.id,
          digest: item.digest,
          importance: item.importance,
          digest_status: item.digest_status,
          digest_tags: _tags,
          enter_into_force: _enter_into_force,
          assigned_to: item.assigned_to,
        }
      })

    updateData(`/radar`, _radars)
      .then((resp) => {
        dispatch({ type: 'SAVEDALL' })
      })
      .catch((error: any) => {})
  }

  const _saveRadar = async (id: number) => {
    const _item = state.radars.find((item) => item.id === id)
    if (_item) {
      const _tags = _item.digest_tags?.join('|') || ''
      const _enter_into_force = _item.enter_into_force?.join('|') || ''
      const _radar = {
        id: _item.id,
        digest: _item.digest,
        importance: _item.importance,
        digest_status: _item.digest_status,
        digest_tags: _tags,
        enter_into_force: _enter_into_force,
        assigned_to: _item.assigned_to,
      }

      return updateData(`/radar/${id}`, _radar)
        .then((resp) => {
          dispatch({ type: 'SAVED', id: id })
          return id
        })
        .catch((error: any) => {
          return Promise.reject(error)
        })
    }

    return Promise.reject(`Item not found. id = ${id}`)
  }

  useEffect(() => {
    loadRadarList()
  }, [])

  return {
    radars: state.radars,
    updateRadarItem: _updateRadarItem,
    saveRadarItems: _saveRadarItems,
    saveRadar: _saveRadar,
    isLoading: state.isLoading,
    isModified: state.isModified,
  }
}
